import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {ScreenEvent} from "@nolimitcity/slot-game/bin/core/screen/event/ScreenEvent";
import {INolimitAudioPlayer} from "@nolimitcity/slot-audio/bin/INolimitAudioPlayer";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {FITHGameMode} from "../gameMode/FITHGameMode";
import {FITHSoundConfig} from "../FITHSoundConfig";
import {FITHParsedInitData} from "../server/data/FITHParsedInitData";
import {FITHGameConfig} from "../FITHGameConfig";

export class FITHSoundManager {
    private static currentLoop: string = "";
    private _mode: string = "";
    private _nextMode: string = "";
    private _isDwarfOnRestore: boolean = false;

    constructor() {
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event: GameEvent) => this.onInitDataParsed(event.params[0]));

        EventHandler.addEventListener(this, ScreenEvent.GAME_READY, () => {
            if (this._mode === FITHGameMode.BONUS && this._nextMode === FITHGameMode.BONUS) {
                if (this._isDwarfOnRestore) {
                    FITHSoundManager.playAmbientLoop(FITHSoundConfig.instance.BONUS_GAME_AMBIANCE_DWARF);
                } else {
                    FITHSoundManager.playAmbientLoop(FITHSoundConfig.instance.BONUS_GAME_AMBIANCE);
                }
            } else {
                FITHSoundManager.playAmbientLoop(FITHSoundConfig.instance.MAIN_GAME_AMBIANCE_1);
            }
        });
    }

    private static get soundPlayer(): INolimitAudioPlayer {
        return <INolimitAudioPlayer>SlotGame.sound;
    }


    private static fadeAndStop(name: string, duration:number, to:number, from?:number){
        this.soundPlayer.fadeTrack({
            name: name,
            from:from,
            to: to,
            duration: duration
        });

        setTimeout(()=>{
            this.soundPlayer.stopTrack(name);
        }, duration)

    }

    public static stopSoundEffect(name: string, fadeOutDuration?: number): void {
        if (fadeOutDuration && fadeOutDuration > 0){
            this.fadeAndStop(name, fadeOutDuration, 0)
        } else  {
            this.soundPlayer.stopTrack(name);
        }
    }

    // --- game sound methods ---
    public static playReelsOut(): void {
        Logger.logDev("sound reelsOut");
        this.playSoundEffect(FITHSoundConfig.instance.REELS_OUT);
    }

    public static playReelsIn(): void {
        Logger.logDev("sound reelsIn");
        this.playSoundEffect(FITHSoundConfig.instance.REELS_IN);
    }

    public static playSymLandingAll(): void {
        Logger.logDev("sound symLandingAll");
        this.playSoundEffect(FITHSoundConfig.instance.SYM_LANDING_ALL);
    }

    public static playSymLandingWood(): void {
        Logger.logDev("sound symLandingWood");
        this.playSoundEffect(FITHSoundConfig.instance.SYM_LANDING_WOOD);
    }

    public static playSymLandingWood2(): void {
        Logger.logDev("sound symLandingWood2");
        this.playSoundEffect(FITHSoundConfig.instance.SYM_LANDING_WOOD_2);
    }

    public static playCountUp(): void {
        Logger.logDev("sound countup");
        this.playSoundEffect(FITHSoundConfig.instance.COUNT_UP);
    }

    public static playCountUpEndsTiny(): void {
        Logger.logDev("sound countupEndsTiny");
        this.playSoundEffect(FITHSoundConfig.instance.COUNT_UP_ENDS_TINY);
    }

    public static playCountUpEndsSmall(): void {
        Logger.logDev("sound countupEndsSmall");
        this.playSoundEffect(FITHSoundConfig.instance.COUNT_UP_ENDS_SMALL);
    }

    public static playCountUpEndsMedium(): void {
        Logger.logDev("sound countupEndsMedium");
        this.playSoundEffect(FITHSoundConfig.instance.COUNT_UP_ENDS_MEDIUM);
    }

    public static playCountUpEndsLarge(): void {
        Logger.logDev("sound countupEndsLarge");
        this.playSoundEffect(FITHSoundConfig.instance.COUNT_UP_ENDS_LARGE);
    }

    public static playTextSweep(): void {
        Logger.logDev("sound textSweep");
        this.playSoundEffect(FITHSoundConfig.instance.TEXT_SWEEP);
    }

    public static playSymbolsShake(): void {
        Logger.logDev("sound symbolsShake");
        this.playSoundEffect(FITHSoundConfig.instance.SYMBOLS_SHAKE);
    }

    public static playSymbolsTurnWild(): void {
        Logger.logDev("sound symbolsTurnWild");
        this.playSoundEffect(FITHSoundConfig.instance.SYMBOLS_TURN_WILD);
    }

    public static playSymbolsExplodeBig(): void {
        Logger.logDev("sound symbolsExplodeBig");
        this.playSoundEffect(FITHSoundConfig.instance.SYMBOLS_EXPLODE_BIG);
    }

    public static playSymbolsExplodeSmall(): void {
        Logger.logDev("sound symbolsExplodeSmall");
        this.playSoundEffect(FITHSoundConfig.instance.SYMBOLS_EXPLODE_SMALL);
    }

    public static playBetlineHighlight(): void {
        Logger.logDev("sound betlineHighlight");
        this.playSoundEffect(FITHSoundConfig.instance.BETLINE_HIGHLIGHT);
    }

    public static playGridDown(): void {
        Logger.logDev("sound gridDown");
        this.playSoundEffect(FITHSoundConfig.instance.GRID_DOWN);
    }

    public static playGridUp(): void {
        Logger.logDev("sound gridUp");
        this.playSoundEffect(FITHSoundConfig.instance.GRID_UP);
    }

    public static playMultiplierFireBall(): void {
        Logger.logDev("sound multiplierFireBall");
        this.playSoundEffect(FITHSoundConfig.instance.MULTIPLIER_FIRE_BALL);
    }

    public static playWildExplode(): void {
        Logger.logDev("sound wildExplode");
        this.playSoundEffect(FITHSoundConfig.instance.WILD_EXPLODE);
    }

    public static playMultiplierUp(): void {
        Logger.logDev("sound multiplierUp");
        this.playSoundEffect(FITHSoundConfig.instance.MULTIPLIER_UP);
    }

    public static playSymLandingWoodSingle(): void {
        Logger.logDev("sound symLandingWoodSingle");
        this.playSoundEffect(FITHSoundConfig.instance.SYM_LANDING_WOOD_SINGLE);
    }

    public static playScatterLands(): void {
        Logger.logDev("sound scatterLands");
        this.playSoundEffect(FITHSoundConfig.instance.SCATTER_LANDS);
    }

    public static playScatterWin(): void {
        Logger.logDev("sound scatterWin");
        this.playSoundEffect(FITHSoundConfig.instance.SCATTER_WIN);
    }

    public static playScatterAnticipation(): void {
        Logger.logDev("sound scatterAnticipation");
        this.playSoundEffect(FITHSoundConfig.instance.SCATTER_ANTICIPATION);
    }

    public static playScatterAnticipationFail(): void {
        Logger.logDev("sound scatterAnticipationFail");
        this.playSoundEffect(FITHSoundConfig.instance.SCATTER_ANTICIPATION_FAIL);
    }

    public static fadeForBigWin(volume: number, duration: number): void {
        this.soundPlayer.fadeTrack({
            name: this.currentLoop,
            to: volume,
            duration: duration
        });
    }

    public static playBigWinLevel(level: number): void {
        Logger.logDev("sound bigWinLevel" + level);
        this.playSoundEffect(FITHSoundConfig.instance.BIG_WIN_LEVEL + level);
    }

    public static playBigWinEnds(): void {
        Logger.logDev("sound bigWinEnds");
        this.playSoundEffect(FITHSoundConfig.instance.BIG_WIN_ENDS);
    }

    public static endForBigWin(volume: number, duration: number): void {
        this.soundPlayer.fadeTrack({
            name: this.currentLoop,
            to: volume,
            duration: duration
        });
    }

    public static endForBigWinInFS(volume: number, duration: number): void {
        this.soundPlayer.fadeTrack({
            name: FITHSoundConfig.instance.BONUS_GAME_AMBIANCE,
            to: volume,
            duration: duration
        });
        this.soundPlayer.fadeTrack({
            name: FITHSoundConfig.instance.BONUS_GAME_AMBIANCE_DWARF,
            to: volume,
            duration: duration
        });
    }

    public static playFSIntroWagonIn(): void {
        Logger.logDev("sound fsIntroWagonIn");
        this.playSoundEffect(FITHSoundConfig.instance.FS_INTRO_WAGON_IN);
    }

    public static playFSIntroText(): void {
        Logger.logDev("sound fsIntroText");
        this.playSoundEffect(FITHSoundConfig.instance.FS_INTRO_TEXT);
    }

    public static playFSIntroWagonOut(): void {
        Logger.logDev("sound fsIntroWagonOut");
        this.playSoundEffect(FITHSoundConfig.instance.FS_INTRO_WAGON_OUT);
    }

    public static playContinueButton(): void {
        Logger.logDev("sound continueButton");
        this.playSoundEffect(FITHSoundConfig.instance.CONTINUE_BUTTON);
    }

    public static switchMainToFS(): void {
        Logger.logDev("sound switching to background-music-fs");

        this.soundPlayer.stopTrack(FITHSoundConfig.instance.MAIN_GAME_AMBIANCE_1);
        this.currentLoop = FITHSoundConfig.instance.BONUS_GAME_AMBIANCE;
        this.soundPlayer.playTrack({name:this.currentLoop, volume:1});

    }

    public static playFSRowExplode(): void {
        Logger.logDev("sound fsRowExplode");
        this.playSoundEffect(FITHSoundConfig.instance.FS_ROW_EXPLODE);
    }

    public static playFSWagonIn(): void {
        Logger.logDev("sound fsWagonIn");
        this.playSoundEffect(FITHSoundConfig.instance.FS_WAGON_IN);
    }

    public static playFS25MultiEnters(): void {
        Logger.logDev("sound fs25MultiEnters");
        this.playSoundEffect(FITHSoundConfig.instance.FS_25_MULTI_ENTERS);
    }

    public static playFS100xEnters(): void {
        Logger.logDev("sound fs100xEnters");
        this.playSoundEffect(FITHSoundConfig.instance.FS_100_X_ENTERS);
    }

    public static playFSDwarfEnters(): void {
        Logger.logDev("sound fsDwarfEnters");
        this.playSoundEffect(FITHSoundConfig.instance.FS_DWARF_ENTERS);
    }

    public static playFSDwarfAnticipation(): void {
        Logger.logDev("sound fsDwarfAnticipation");
        this.playSoundEffect(FITHSoundConfig.instance.FS_DWARF_ANTICIPATION);
    }

    public static playFSChestEnters(): void {
        Logger.logDev("sound fsChestEnters");
        this.playSoundEffect(FITHSoundConfig.instance.FS_CHEST_ENTERS);
    }

    public static playFSChestAnticipation(): void {
        Logger.logDev("sound fsChestAnticipation");
        this.playSoundEffect(FITHSoundConfig.instance.FS_CHEST_ANTICIPATION);
    }

    public static playFSReelsSpinning(): void {
        Logger.logDev("sound fsReelsSpinning");
        this.playSoundEffect(FITHSoundConfig.instance.FS_REELS_SPINNING);
    }

    public static playFSReelsLanding(): void {
        Logger.logDev("sound fsReelsLanding");
        this.playSoundEffect(FITHSoundConfig.instance.FS_REELS_LANDING);
    }

    public static playFSWagonOut(): void {
        Logger.logDev("sound fsWagonOut");
        this.playSoundEffect(FITHSoundConfig.instance.FS_WAGON_OUT);
    }

    public static playFSCoinLandingTiny(): void {
        Logger.logDev("sound fsCoinLandingTiny");
        this.playSoundEffect(FITHSoundConfig.instance.FS_COIN_LANDING_TINY);
    }

    public static playFSCoinLandingSmall(): void {
        Logger.logDev("sound fsCoinLandingSmall");
        this.playSoundEffect(FITHSoundConfig.instance.FS_COIN_LANDING_SMALL);
    }

    public static playFSCoinLandingMedium(): void {
        Logger.logDev("sound fsCoinLandingMedium");
        this.playSoundEffect(FITHSoundConfig.instance.FS_COIN_LANDING_MEDIUM);
    }

    public static playFSCoinLandingLarge(): void {
        Logger.logDev("sound fsCoinLandingLarge");
        this.playSoundEffect(FITHSoundConfig.instance.FS_COIN_LANDING_LARGE);
    }

    public static playFSCoinLandingChest(): void {
        Logger.logDev("sound fsCoinLandingChest");
        this.playSoundEffect(FITHSoundConfig.instance.FS_COIN_LANDING_CHEST);
    }

    public static playFSCoinLandingDwarf(): void {
        Logger.logDev("sound fsCoinLandingDwarf");
        this.playSoundEffect(FITHSoundConfig.instance.FS_COIN_LANDING_DWARF);
    }

    public static playFSSpinUpgrade(): void {
        Logger.logDev("sound fsSpinUpgrade");
        this.playSoundEffect(FITHSoundConfig.instance.FS_SPIN_UPGRADE);
    }

    public static playFSMultiLandsOnCoinTiny(): void {
        Logger.logDev("sound fsMultiLandsOnCoinTiny");
        this.playSoundEffect(FITHSoundConfig.instance.FS_MULTI_LANDS_ON_COIN_TINY);
    }

    public static playFSMultiLandsOnCoinSmall(): void {
        Logger.logDev("sound fsMultiLandsOnCoinSmall");
        this.playSoundEffect(FITHSoundConfig.instance.FS_MULTI_LANDS_ON_COIN_SMALL);
    }

    public static playFSMultiLandsOnCoinMedium(): void {
        Logger.logDev("sound fsMultiLandsOnCoinMedium");
        this.playSoundEffect(FITHSoundConfig.instance.FS_MULTI_LANDS_ON_COIN_MEDIUM);
    }

    public static playFSMultiLandsOnCoinLarge(): void {
        Logger.logDev("sound fsMultiLandsOnCoinLarge");
        this.playSoundEffect(FITHSoundConfig.instance.FS_MULTI_LANDS_ON_COIN_LARGE);
    }

    public static playFSMultiDownToCoin(): void {
        Logger.logDev("sound fsMultiDownToCoin");
        this.playSoundEffect(FITHSoundConfig.instance.FS_MULTI_DOWN_TO_COIN);
    }

    public static playFSDynamiteWagonHighlight(): void {
        Logger.logDev("sound fsDynamiteWagoHiglight");
        this.playSoundEffect(FITHSoundConfig.instance.FS_DYNAMITE_WAGON_HIGHLIGHT);
    }

    public static playFSDynamiteLandsOnCoin(): void {
        Logger.logDev("sound fsDynamiteLandsOnCoin");
        this.playSoundEffect(FITHSoundConfig.instance.FS_DYNAMITE_LANDS_ON_COIN);
    }

    public static playFSDynamiteThrow(): void {
        Logger.logDev("sound fsDynamiteThrow");
        this.playSoundEffect(FITHSoundConfig.instance.FS_DYNAMITE_THROW);
    }

    public static playFSDynamiteStone(count: number): void {
        Logger.logDev("sound fsDynamiteStone" + count);
        this.playSoundEffect(FITHSoundConfig.instance.FS_DYNAMITE_STONE + count);
    }

    public static playFSDynamiteChest(count: number): void {
        Logger.logDev("sound fsDynamiteChest" + count);
        this.playSoundEffect(FITHSoundConfig.instance.FS_DYNAMITE_CHEST + count);
    }

    public static playFSCoinHighlight(): void {
        Logger.logDev("sound fsCoinHighlight");
        this.playSoundEffect(FITHSoundConfig.instance.FS_COIN_HIGHLIGHT);
    }

    public static playFSMultiDownToChest(): void {
        Logger.logDev("sound fsMultiDownToChest");
        this.playSoundEffect(FITHSoundConfig.instance.FS_MULTI_DOWN_TO_CHEST);
    }

    public static playFSMultiLandsInChest(): void {
        Logger.logDev("sound fsMultiLandsInChest");
        this.playSoundEffect(FITHSoundConfig.instance.FS_MULTI_LANDS_IN_CHEST);
    }

    public static playFSCoinSweep(): void {
        Logger.logDev("sound fsCoinSweep");
        this.playSoundEffect(FITHSoundConfig.instance.FS_COIN_SWEEP);
    }

    public static playFSBagLands(): void {
        Logger.logDev("sound fsBagLands");
        this.playSoundEffect(FITHSoundConfig.instance.FS_BAG_LANDS);
    }

    public static playFSChestLands(): void {
        Logger.logDev("sound fsChestLands");
        this.playSoundEffect(FITHSoundConfig.instance.FS_CHEST_LANDS);
    }

    public static switchFSToFSDwarf(): void {
        Logger.logDev("sound switching to background-music-fs-dwarf");

        this.soundPlayer.stopTrack(FITHSoundConfig.instance.BONUS_GAME_AMBIANCE);
        this.currentLoop = FITHSoundConfig.instance.BONUS_GAME_AMBIANCE_DWARF;
        this.soundPlayer.playTrack({name:this.currentLoop})
    }

    public static playFSDwarfLands(): void {
        Logger.logDev("sound fsDwarfLands");
        this.playSoundEffect(FITHSoundConfig.instance.FS_DWARF_LANDS);
    }

    public static playFSDwarfCollect(): void {
        Logger.logDev("sound fsDwarfCollect");
        this.playSoundEffect(FITHSoundConfig.instance.FS_DWARF_COLLECT);
    }

    public static playFSDwarfMultiLands(): void {
        Logger.logDev("sound fsDwarfMultiLands");
        this.playSoundEffect(FITHSoundConfig.instance.FS_DWARF_MULTI_LANDS);
    }

    public static playFSMultiToDwarf(): void {
        Logger.logDev("sound fsMultiToDwarf");
        this.playSoundEffect(FITHSoundConfig.instance.FS_MULTI_TO_DWARF);
    }

    public static playFSEnds(): void {
        Logger.logDev("sound fsEnds");
        this.playSoundEffect(FITHSoundConfig.instance.FS_ENDS);
    }

    public static playFSEndsCountUpEnds(): void {
        Logger.logDev("sound fsEndsCountupEnds");
        this.playSoundEffect(FITHSoundConfig.instance.FS_ENDS_COUNT_UP_ENDS);
    }

    public static fadeForBeerTime(volume: number, duration: number): void {
        this.soundPlayer.fadeTrack({
            name: this.currentLoop,
            to: volume,
            duration: duration
        });

        this.playBeerTimeStart();
    }

    public static playBeerTimeCountUp(): void {
        Logger.logDev("sound beerTimeCountup");
        this.playSoundEffect(FITHSoundConfig.instance.BEER_TIME_COUNT_UP);
    }

    public static playBeerTimeCountUpEnds(): void {
        Logger.logDev("sound beerTimeCountupEnds");
        this.playSoundEffect(FITHSoundConfig.instance.BEER_TIME_COUNT_UP_ENDS);
    }

    public static playBeerTimeMusic(): void {
        Logger.logDev("sound beerTimeMusic");
        this.playSoundEffect(FITHSoundConfig.instance.BEER_TIME_MUSIC);
    }

    public static playBeerTimeEnds(): void {
        Logger.logDev("sound beerTimeEnds");
        this.playSoundEffect(FITHSoundConfig.instance.BEER_TIME_ENDS);
    }

    public static switchBeerTimeToMain(): void {
        Logger.logDev("sound switching to background-music-main");

        this.fadeAndStop(FITHSoundConfig.instance.BEER_TIME_MUSIC,100,0);

        this.currentLoop = FITHSoundConfig.instance.MAIN_GAME_AMBIANCE_1;
        this.soundPlayer.playTrack({name: this.currentLoop,volume: 0});
        this.soundPlayer.fadeTrack({name:this.currentLoop, from:0, to:1,duration:2000});
    }

    public static switchFStoMain(): void {
        Logger.logDev("sound switching to background-music-main");

        this.fadeAndStop(FITHSoundConfig.instance.BONUS_GAME_AMBIANCE,100,0);
        this.fadeAndStop(FITHSoundConfig.instance.BONUS_GAME_AMBIANCE_DWARF,100,0);

        this.currentLoop = FITHSoundConfig.instance.MAIN_GAME_AMBIANCE_1;
        this.soundPlayer.playTrack({name: this.currentLoop,volume: 0});
        this.soundPlayer.fadeTrack({name:this.currentLoop, from:0, to:1,duration:2000});

    }

    // --- internal methods ---

    private static playAmbientLoop(name: string): void {
        this.currentLoop = name;
        this.soundPlayer.playTrack({
            name: this.currentLoop
        });
    }

    public static playSoundEffect(name: string, repeat?: number, volume?: number): void {
        this.soundPlayer.playTrack({
            name: name,
            repeat: repeat,
            volume: volume
        });
    }

    private static playBeerTimeStart(): void {
        Logger.logDev("sound beerTimeStart");
        this.playSoundEffect(FITHSoundConfig.instance.BEER_TIME_START);
    }

    /**
     * @description When init data is parsed.
     * @param data
     */
    private onInitDataParsed(data: FITHParsedInitData): void {
        this._mode = data.mode;
        this._nextMode = data.nextMode;

        EventHandler.removeEventListener(this, ServerEvent.INIT_DATA_PARSED);

        // #290
        const originalReels: number = FITHGameConfig.instance.REELS_NUM_ORIGINAL;
        this._isDwarfOnRestore = false;
        for (let i: number = 0; i < originalReels; i++) {
            // scan middle 4 rows
            for (let j: number = 1; j < data.reels[i].length - 1; j++) {
                if (data.reels[(i * originalReels) + j][0] === "DW") {
                    this._isDwarfOnRestore = true;

                    return;
                }
            }
        }
    }
}